import type { ComponentDiagnostic } from "@swot/base-components";
import { throwCmtsCanonicalError } from "@cmts/api/cmtsApiErrors.js";

export type CmtsEnvironmentMode = "live" | "demo" | "test";
export type CmtsAppEnvironment = "local" | "demo" | "staging" | "uat" | "production";
export type CmtsRuntimeMode = "LIVE_BACKEND" | "OFFLINE_DEMO" | "AUTO";
export type CmtsResolvedRuntimeMode = "LIVE_BACKEND" | "OFFLINE_DEMO";

export type CmtsHostAppConfig = {
  appTitle: string;
  appSubtitle: string;
  apiBaseUrl: string;
  tenantId?: string | undefined;
  tenantCode?: string | undefined;
  defaultLandingPath: string;
  requestTimeoutMs: number;
  environmentMode: CmtsEnvironmentMode;
  appEnvironment: CmtsAppEnvironment;
  runtimeMode: CmtsRuntimeMode;
  resolvedRuntimeMode: CmtsResolvedRuntimeMode;
  enableOfflineDemo: boolean;
  enableDemoPersonaLogin: boolean;
  enableDemoSeedReset: boolean;
  enableLiveBackend: boolean;
  enableDemoFallbacks: boolean;
  enableRuntimeEndpointCoveragePage: boolean;
  enableOperationalReadinessPage: boolean;
};

export const defaultCmtsHostAppConfig: CmtsHostAppConfig = {
  appTitle: "SWOT DigiNXT CMTS",
  appSubtitle: "Complaint Management and Tracking System",
  apiBaseUrl: "/api",
  defaultLandingPath: "/cmts/my-work",
  requestTimeoutMs: 30000,
  environmentMode: "live",
  appEnvironment: "local",
  runtimeMode: "AUTO",
  resolvedRuntimeMode: "LIVE_BACKEND",
  enableOfflineDemo: false,
  enableDemoPersonaLogin: false,
  enableDemoSeedReset: false,
  enableLiveBackend: true,
  enableDemoFallbacks: false,
  enableRuntimeEndpointCoveragePage: true,
  enableOperationalReadinessPage: true,
};

export function resolveCmtsRuntimeMode(config: Pick<CmtsHostAppConfig, "appEnvironment" | "runtimeMode" | "apiBaseUrl" | "enableOfflineDemo" | "enableLiveBackend">): CmtsResolvedRuntimeMode {
  if (config.appEnvironment === "production") return "LIVE_BACKEND";
  if (config.runtimeMode === "LIVE_BACKEND") return "LIVE_BACKEND";
  if (config.runtimeMode === "OFFLINE_DEMO") {
    if (config.enableOfflineDemo) return "OFFLINE_DEMO";
    return "LIVE_BACKEND";
  }
  if (config.appEnvironment === "staging" || config.appEnvironment === "uat") return "LIVE_BACKEND";
  const apiMissing = !config.apiBaseUrl || config.apiBaseUrl === "/api" || config.apiBaseUrl.trim() === "";
  if ((config.appEnvironment === "local" || config.appEnvironment === "demo") && config.enableOfflineDemo && apiMissing) return "OFFLINE_DEMO";
  return config.enableLiveBackend ? "LIVE_BACKEND" : "OFFLINE_DEMO";
}

export function createCmtsHostAppConfig(overrides: Partial<CmtsHostAppConfig> = {}): CmtsHostAppConfig {
  const preConfig = { ...defaultCmtsHostAppConfig, ...overrides };
  const config = { ...preConfig, resolvedRuntimeMode: resolveCmtsRuntimeMode(preConfig) };
  const diagnostics = validateCmtsHostAppConfig(config);
  const blocking = diagnostics.filter((item) => item.severity === "error");
  if (blocking.length > 0) {
    throwCmtsCanonicalError({ code: "CMTS_ERR_FAILED_VALIDATION", message: `CMTS host configuration is invalid: ${blocking.map((item) => item.message).join("; ")}`, details: { diagnostics: blocking.map((item) => ({ code: item.code, message: item.message })) } });
  }
  return config;
}

export function validateCmtsHostAppConfig(config: CmtsHostAppConfig): readonly ComponentDiagnostic[] {
  const diagnostics: ComponentDiagnostic[] = [];
  if (!config.apiBaseUrl || !config.apiBaseUrl.trim()) {
    diagnostics.push({ code: "CMTS_CONFIG_API_BASE_URL_REQUIRED", message: "CMTS API base URL is required.", severity: "error", remediation: "Set apiBaseUrl from the host environment." });
  }
  if (!config.appTitle.trim()) {
    diagnostics.push({ code: "CMTS_CONFIG_TITLE_REQUIRED", message: "CMTS app title is required.", severity: "error" });
  }
  if (config.requestTimeoutMs < 5000) {
    diagnostics.push({ code: "CMTS_CONFIG_TIMEOUT_TOO_LOW", message: "CMTS timeout below 5 seconds can create false failures for evidence/report APIs.", severity: "warning", remediation: "Use at least 30000 ms for enterprise deployments." });
  }
  if (config.environmentMode === "live" && config.enableDemoFallbacks) {
    diagnostics.push({ code: "CMTS_CONFIG_DEMO_FALLBACK_LIVE", message: "Demo fallback data is enabled in live mode.", severity: "error", remediation: "Disable enableDemoFallbacks for live deployments." });
  }
  if (config.appEnvironment === "production") {
    if (config.runtimeMode !== "LIVE_BACKEND" || config.resolvedRuntimeMode !== "LIVE_BACKEND") diagnostics.push({ code: "CMTS_RUNTIME_PRODUCTION_MUST_BE_LIVE", message: "Production must run only in LIVE_BACKEND mode.", severity: "error" });
    if (config.enableOfflineDemo) diagnostics.push({ code: "CMTS_RUNTIME_PRODUCTION_OFFLINE_DISABLED", message: "Offline demo must be disabled in production.", severity: "error" });
    if (config.enableDemoPersonaLogin) diagnostics.push({ code: "CMTS_RUNTIME_PRODUCTION_DEMO_LOGIN_DISABLED", message: "Demo persona login must be disabled in production.", severity: "error" });
    if (config.enableDemoSeedReset) diagnostics.push({ code: "CMTS_RUNTIME_PRODUCTION_SEED_RESET_DISABLED", message: "Seed reset must be disabled in production.", severity: "error" });
    if (config.enableDemoFallbacks) diagnostics.push({ code: "CMTS_RUNTIME_PRODUCTION_DEMO_FALLBACK_DISABLED", message: "Demo fallback data must be disabled in production.", severity: "error" });
  }
  if (!config.tenantCode && !config.tenantId) {
    diagnostics.push({ code: "CMTS_CONFIG_TENANT_SESSION", message: "Tenant is resolved from the authenticated CMTS session or tenant code login.", severity: "info" });
  }
  if (config.tenantId && !config.tenantCode) {
    diagnostics.push({ code: "CMTS_CONFIG_TENANT_ID_LEGACY", message: "A legacy tenant id is configured without a tenant code. Prefer VITE_CMTS_DEFAULT_TENANT_CODE for login.", severity: "warning", remediation: "Use tenant code for login and reserve tenant id for backend returned tenant context." });
  }
  if (config.resolvedRuntimeMode === "OFFLINE_DEMO") {
    diagnostics.push({ code: "CMTS_RUNTIME_OFFLINE_DEMO", message: "Offline Demo Mode is active. Data is browser-local and not connected to production backend.", severity: "warning", remediation: "Use LIVE_BACKEND for staging/UAT/production integration." });
  }
  return diagnostics;
}
